import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Search,
  Truck,
  ChevronRight,
  Mail,
  Phone
} from 'lucide-react';
import { Box } from '@mui/material';
import { useAuth } from '@/shared/contexts';
import {
  superAdminApi,
  type SuperAdminSupplierOption
} from '@/pages/SuperAdmin/api/superAdminApi';
import { SaDataTable, type SaColumn } from '@/pages/SuperAdmin/SaDataTable';
import { SaStatusChip } from '@/pages/SuperAdmin/SaStatusChip';
import { Alert } from '@/shared/components/ui/Alert';
import { Input } from '@/shared/components/ui/Input';
import {
  OrderCreationCodeDialog,
  type OrderSessionPayload
} from '@/pages/SuperAdmin/OrderCreationCodeDialog';

export const SuperAdminOrdersPage: React.FC = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [suppliers, setSuppliers] = useState<SuperAdminSupplierOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingSupplierId, setPendingSupplierId] = useState<string | null>(
    null
  );

  const openOrderFor = (supplierId: string) => {
    setPendingSupplierId(supplierId);
  };

  const handleSessionSuccess = (session: OrderSessionPayload) => {
    const id = session.supplier.id;
    setPendingSupplierId(null);
    navigate(`/super-admin/orders/${id}`, {
      state: { orderSession: session }
    });
  };

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await superAdminApi.listSuppliers(
          accessToken,
          search || undefined
        );
        if (!cancelled) setSuppliers(res.data);
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || 'Failed to load suppliers';
          setError(
            Array.isArray(message) ? message.join(', ') : String(message)
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [accessToken, search]);

  const columns = useMemo<SaColumn<SuperAdminSupplierOption>[]>(
    () => [
      {
        id: 'supplier',
        label: 'Supplier',
        width: '34%',
        render: s => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'rgba(0,107,130,0.1)',
                color: '#006B82',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Truck className="h-5 w-5" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="p"
                sx={{
                  m: 0,
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {s.businessName || 'Unnamed supplier'}
              </Box>
              <Box component="p" sx={{ m: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
                {s.id.slice(0, 8)}…
              </Box>
            </Box>
          </Box>
        )
      },
      {
        id: 'contact',
        label: 'Contact',
        width: '34%',
        render: s => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            {s.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--color-text-secondary)' }}>
                <Mail className="h-3.5 w-3.5 text-text-muted shrink-0" />
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {s.email}
                </Box>
              </Box>
            )}
            {s.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--color-text-secondary)' }}>
                <Phone className="h-3.5 w-3.5 text-text-muted shrink-0" />
                <span>{s.phone}</span>
              </Box>
            )}
            {!s.email && !s.phone && <span>—</span>}
          </Box>
        )
      },
      {
        id: 'status',
        label: 'Status',
        width: '14%',
        render: s => <SaStatusChip status={s.status} />
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'left',
        width: 120,
        minWidth: 120,
        render: s => (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              openOrderFor(s.id);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 8,
              background: '#006B82',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              padding: '8px 12px',
              border: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Open
            <ChevronRight className="h-4 w-4" />
          </button>
        )
      }
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
          Orders
        </h2>
        <p className="mt-1 text-sm text-text-tertiary">
          Choose a supplier to compose an order in this workspace
        </p>
      </div>

      <div className="rounded-2xl border border-border-primary bg-background-card p-4 shadow-sm">
        <label className="mb-1 block text-xs font-medium text-text-tertiary">
          Search suppliers
        </label>
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Business name or email…"
            className="bg-background-card pl-9"
          />
        </div>
      </div>

      {error && <Alert variant="destructive">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center rounded-2xl border border-border-primary bg-background-card py-16 shadow-sm">
          <Loader2 className="h-7 w-7 animate-spin text-[#006B82]" />
        </div>
      ) : (
        <SaDataTable
          columns={columns}
          rows={suppliers}
          getRowId={row => row.id}
          emptyMessage="No active suppliers found"
          onRowClick={row => openOrderFor(row.id)}
        />
      )}

      {pendingSupplierId && (
        <OrderCreationCodeDialog
          open
          supplierId={pendingSupplierId}
          onClose={() => setPendingSupplierId(null)}
          onSuccess={handleSessionSuccess}
        />
      )}
    </div>
  );
};
